import React from "react";
import "./skeletonloader.css";

/**
 * Universal, highly flexible Skeleton Loader component supporting multiple layout types:
 * - "table" (default): Tabular rows and columns with avatar and action placeholders.
 * - "card": A responsive grid of card layouts with images, text blocks, and actions.
 * - "list": Vertical layout of simple list items with icons and double text rows.
 * - "form": Layout simulating text labels and rectangular input containers.
 * - "chart": Loading layout for graphical bars or visual doughnut chart data summaries.
 * 
 * Props:
 * - type: "table" | "card" | "list" | "form" | "chart" | "slider" (default: "table")
 * - count: Number of items for lists, cards, form fields, or slider cards (default: 4)
 * - rows: Number of skeleton rows for table view (default: 5)
 * - cols: Number of columns for table view (default: 5)
 * - hasHeader: Whether to render a skeleton header row for table view (default: true)
 */
function SkeletonLoader({ type = "table", count = 4, rows = 5, cols = 5, hasHeader = true }) {
  // Staggered width percentages to simulate organic text listings
  const textWidths = ["45%", "65%", "75%", "50%", "85%", "60%", "70%"];

  // 1. Tabular Layout
  const renderTable = () => (
    <div className="skeleton-table-container" aria-hidden="true">
      {hasHeader && (
        <div className="skeleton-table-header">
          {Array.from({ length: cols }).map((_, index) => (
            <div key={`head-${index}`} className="skeleton-cell">
              <div className="skeleton-bar header-bar" style={{ width: index === 0 ? "35%" : "55%" }} />
            </div>
          ))}
        </div>
      )}
      <div className="skeleton-table-body">
        {Array.from({ length: rows }).map((_, rIdx) => (
          <div key={`row-${rIdx}`} className="skeleton-table-row">
            {Array.from({ length: cols }).map((_, cIdx) => {
              if (cIdx === 0) {
                return (
                  <div key={`cell-${rIdx}-${cIdx}`} className="skeleton-cell flex-cell">
                    <div className="skeleton-avatar" />
                    <div className="skeleton-bar" style={{ width: "45%" }} />
                  </div>
                );
              } else if (cIdx === cols - 1) {
                return (
                  <div key={`cell-${rIdx}-${cIdx}`} className="skeleton-cell flex-cell justify-end">
                    <div className="skeleton-action" />
                    <div className="skeleton-action" />
                  </div>
                );
              } else {
                const randomWidth = textWidths[(rIdx + cIdx) % textWidths.length];
                return (
                  <div key={`cell-${rIdx}-${cIdx}`} className="skeleton-cell">
                    <div className="skeleton-bar" style={{ width: randomWidth }} />
                  </div>
                );
              }
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // 2. Responsive Card Grid Layout
  const renderCards = () => (
    <div className="skeleton-card-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`card-${idx}`} className="skeleton-card">
          <div className="skeleton-card-image" />
          <div className="skeleton-card-content">
            <div className="skeleton-card-header-row">
              <div className="skeleton-circle-avatar" />
              <div className="skeleton-bar" style={{ width: "60%" }} />
            </div>
            <div className="skeleton-bar" style={{ width: "90%", marginTop: "12px" }} />
            <div className="skeleton-bar" style={{ width: "70%", marginTop: "8px" }} />
            <div className="skeleton-card-footer">
              <div className="skeleton-bar" style={{ width: "30%" }} />
              <div className="skeleton-action" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // 3. Simple Vertical List Layout
  const renderList = () => (
    <div className="skeleton-list-container" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`list-item-${idx}`} className="skeleton-list-item">
          <div className="skeleton-circle-avatar" />
          <div className="skeleton-list-details">
            <div className="skeleton-bar" style={{ width: "40%" }} />
            <div className="skeleton-bar" style={{ width: "65%", marginTop: "8px", height: "10px" }} />
          </div>
        </div>
      ))}
    </div>
  );

  // 4. Input Fields / Form Layout
  const renderForm = () => (
    <div className="skeleton-form-container" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`form-field-${idx}`} className="skeleton-form-field">
          <div className="skeleton-bar" style={{ width: "25%", height: "10px", marginBottom: "8px" }} />
          <div className="skeleton-form-input-box" />
        </div>
      ))}
    </div>
  );

  // 5. Horizontal Slider Cards Layout (like Script Management)
  const renderSlider = () => (
    <div className="skeleton-slider-container" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`slider-card-${idx}`} className="skeleton-slider-card">
          <div className="skeleton-slider-header">
            <div className="skeleton-bar slider-title-bar" />
            <div className="skeleton-slider-actions">
              <div className="skeleton-action" />
              <div className="skeleton-action" />
              <div className="skeleton-action" />
            </div>
          </div>
          <div className="skeleton-slider-body">
            <div className="skeleton-bar slider-body-bar" style={{ width: "85%" }} />
            <div className="skeleton-bar slider-body-bar" style={{ width: "60%" }} />
            <div className="skeleton-bar slider-body-bar" style={{ width: "70%" }} />
            <div className="skeleton-bar slider-body-bar" style={{ width: "40%" }} />
          </div>
          <div className="skeleton-slider-footer">
            <div className="skeleton-bar" style={{ width: "35%", height: "10px" }} />
            <div className="skeleton-bar" style={{ width: "25%", height: "10px" }} />
          </div>
        </div>
      ))}
    </div>
  );

  // 6. Visual Graphical Chart Layout
  const renderChart = () => (
    <div className="skeleton-chart-container" aria-hidden="true">
      <div className="skeleton-chart-circular-wrapper">
        <div className="skeleton-circle-chart" />
        <div className="skeleton-chart-legend">
          <div className="skeleton-bar" style={{ width: "80px", marginBottom: "8px" }} />
          <div className="skeleton-bar" style={{ width: "110px", marginBottom: "8px" }} />
          <div className="skeleton-bar" style={{ width: "60px" }} />
        </div>
      </div>
      <div className="skeleton-chart-bars">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={`chart-bar-${idx}`} className="skeleton-chart-bar-col">
            <div className="skeleton-chart-vertical-bar" style={{ height: `${20 + idx * 25}%` }} />
            <div className="skeleton-bar" style={{ width: "30px", marginTop: "10px", height: "8px" }} />
          </div>
        ))}
      </div>
    </div>
  );

  // Trigger correct layout engine based on 'type' prop
  switch (type) {
    case "card":
      return renderCards();
    case "list":
      return renderList();
    case "form":
      return renderForm();
    case "chart":
      return renderChart();
    case "slider":
      return renderSlider();
    case "table":
    default:
      return renderTable();
  }
}

export default SkeletonLoader;
